import { FC } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Checkbox,
  tailwindOverride,
  TableHeadCell,
} from '@getsynapse/design-system';
import { useSelector, useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import { selectIsUserLd } from 'state/User/userSlice';
import { REQUEST_TABLE_COLUMNS } from 'utils/constants/request';
import { SORT_ORDER } from 'utils/constants';
import { SortingType } from 'utils/customTypes';
import {
  setSortParameter,
  requestsSelectors,
  getRequests,
} from 'state/normalized/requests/requestsSlice';

const useRequestHeaderCells = (isLearningUser: boolean) => {
  const { consolidatedRequestsPage = false } = useFlags();

  const baseCellKeys = [
    'REQUEST_NO',
    'REQUEST_TITLE',
    'STATUS',
    'REQUESTER_NAME',
    'OWNER_NAME',
    'LINKED_PROJECTS',
    'FORM',
    'CREATION_DATE',
    'SUBMISSION_DATE',
    'DECISION_DATE',
    'UPDATE_DATE',
  ];

  if (isLearningUser) {
    baseCellKeys.splice(3, 0, 'PRIORITY');
    baseCellKeys.splice(7, 0, 'BUSINESS_UNIT');
  }

  return baseCellKeys.map((key) => ({
    content: intl.get(`REQUESTS_LIST_PAGE.TABLE.HEAD.${key}`),
    columnName: consolidatedRequestsPage
      ? REQUEST_TABLE_COLUMNS[key as keyof typeof REQUEST_TABLE_COLUMNS]
      : '',
  }));
};

type RequestsTableHeaderProps = {
  handleSelectRowHeader: (checked: boolean | 'mixed') => void;
  rowHeaderCheckboxMode: boolean | 'mixed';
};

const RequestsTableHeader: FC<RequestsTableHeaderProps> = ({
  handleSelectRowHeader,
  rowHeaderCheckboxMode,
}) => {
  const dispatch = useDispatch();

  const isLearningUser = useSelector(selectIsUserLd);
  const filterParams = useSelector(requestsSelectors.selectFilterParameter);
  const sortParameter = useSelector(requestsSelectors.selectSortParameter);

  const headerCells: {
    content: React.ReactNode;
    className?: string;
    columnName?: string;
  }[] = useRequestHeaderCells(isLearningUser);
  const { consolidatedRequestsPage = false } = useFlags();

  const handleSort = async (columnName: string, sortOrder: SortingType) => {
    await dispatch(setSortParameter({ sortBy: columnName, sortOrder }));
    dispatch(
      getRequests({
        ...filterParams,
      })
    );
  };

  if (consolidatedRequestsPage) {
    headerCells.unshift({
      content: (
        <Checkbox
          onChange={(event) => handleSelectRowHeader(event.target.checked)}
          label=''
          inputProps={{
            className: 'mr-0 mt-0 ml-1',
          }}
          checked={rowHeaderCheckboxMode}
        />
      ),
      className: 'z-2 left-0',
    });
    headerCells.push({
      content: '',
      className: 'right-0 z-2',
    });
  }
  const stickyHeaderStyles = 'sticky top-0 z-1 bg-primary-lightest';

  return (
    <thead className='h-10 text-primary text-caption'>
      <tr>
        {headerCells.map((cell, index) => {
          const isActive = sortParameter.sortBy === cell.columnName;
          const isDesc = isActive && sortParameter.sortOrder === 'desc';
          const isAsc = isActive && sortParameter.sortOrder === 'asc';
          return (
            <TableHeadCell
              key={index}
              columnName={cell.columnName}
              content={cell.content}
              className={tailwindOverride(
                stickyHeaderStyles,
                {
                  'bg-secondary-lightest': isActive,
                },
                cell.className
              )}
              handleSort={() => {
                handleSort(
                  cell.columnName!,
                  isAsc ? SORT_ORDER.DESC : SORT_ORDER.ASC
                );
              }}
              isActive={isActive}
              isDesc={isDesc}
              isAsc={isAsc}
              aria-label={cell.columnName}
            />
          );
        })}
      </tr>
    </thead>
  );
};

export default RequestsTableHeader;
